import axios from 'axios';

const API_BASE_URL = 'https://pia.undp.org.rs';

// Create axios instance
const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for handling auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('accessToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Types
export interface AdminUser {
  id: number;
  email: string;
  full_name: string;
  is_active: boolean;
}

export interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  country: string;
  timezone: string;
  telegram_registration_code?: string;
  telegram_user_code?: string;
  status: 'PENDING' | 'ACTIVE' | 'INACTIVE' | 'REVOKED';
  auto_reminder_enabled: boolean;
  auto_reminder_days: number;
  last_auto_reminder_at?: string | null;
  created_at: string;
}

export interface Admin {
  id: number;
  email: string;
  full_name: string;
  is_active: boolean;
  created_at: string;
}

export interface Survey {
  id: number;
  title: string;
  description?: string;
  status: 'draft' | 'active' | 'archived';
  periodicity_days: number;
  portfolio_ref?: string;
  context_version_id?: number;
  created_at: string;
  updated_at?: string;
  questions?: Question[];
}

export interface Question {
  id: number;
  survey_id: number;
  type: 'text' | 'multiple_choice' | 'rating' | 'yes_no';
  text: string;
  answer_criteria?: string;
  instructions?: string;
  order: number;
  is_required: boolean;
  created_at: string;
}

export interface ContextVersion {
  id: number;
  survey_id: number;
  context_fields_json?: any;
  context_notes_md?: string;
  attachments?: string[];
  status: 'draft' | 'preview' | 'active';
  created_at: string;
  updated_at?: string;
}

export interface ContextVersionHistory {
  id: number;
  context_version_id: number;
  previous_context_notes_md?: string;
  previous_context_fields_json?: any;
  previous_attachments?: string[];
  previous_status: 'draft' | 'preview' | 'active';
  action: string;
  changed_by_admin_id: number;
  created_at: string;
}

export interface Assignment {
  id: number;
  user_id: number;
  survey_id: number;
  status: 'assigned' | 'in_progress' | 'completed' | 'overdue';
  assigned_at: string;
  last_reminder_at?: string;
  completed_at?: string;
  user_name?: string;
  survey_title?: string;
}

export interface BulkAssignmentResponse {
  created: number;
  skipped: number;
  assignments: Assignment[];
}

export interface RegistrationCode {
  id: number;
  code: string;
  expires_at?: string;
  status: 'active' | 'used' | 'expired' | 'revoked';
  used_by_user_id?: number;
  created_at: string;
  used_at?: string;
}

export interface AnalyticsOverview {
  total_users: number;
  active_users: number;
  total_surveys: number;
  active_surveys: number;
  total_responses: number;
  total_assignments: number;
  completion_rate: number;
  responses_this_week: number;
  responses_today: number;
  active_users_this_week: number;
  active_users_today: number;
  sessions_completed_this_week: number;
  sessions_completed_total: number;
}

export interface SurveyAnalytics {
  survey_id: number;
  survey_title: string;
  total_assignments: number;
  sessions_started: number;
  sessions_completed: number;
  completion_rate: number;
  avg_response_time_hours?: number;
  total_responses: number;
}

export interface UserEngagement {
  date: string;
  active_users: number;
  messages_sent: number;
  responses_submitted: number;
}

export interface UploadFile {
  id: number;
  user_id: number;
  survey_id?: number;
  filename: string;
  content_type: string;
  size_bytes: number;
  storage_uri: string;
  created_at: string;
}

export interface Paginated<T> {
  items: T[];
  total: number;
}

export interface UserDeletionImpact {
  user_id: number;
  full_name: string;
  responses: number;
  conversation_messages: number;
  message_logs: number;
  uploads: number;
  transcripts: number;
  assignments: number;
  reminder_preferences: number;
  registration_codes: number;
}

export interface MessageLog {
  id: number;
  user_id: number;
  direction: string;
  message_type: string;
  content?: string;
  telegram_message_id?: string;
  timestamp: string;
  error_message?: string;
}

// API Services
export const authApi = {
  login: async (email: string, password: string): Promise<{ access_token: string; token_type: string }> => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);
    
    const response = await api.post('/auth/login', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getCurrentAdmin: async (): Promise<AdminUser> => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  createAdmin: async (data: { email: string; password: string; full_name: string }): Promise<AdminUser> => {
    const response = await api.post('/auth/create-admin', data);
    return response.data;
  },

  resetPassword: async (admin_id: number, new_password: string): Promise<void> => {
    await api.put('/auth/reset-password', { admin_id, new_password });
  },

  listAdmins: async (): Promise<AdminUser[]> => {
    const response = await api.get('/auth/admins');
    return response.data;
  },

  toggleAdminStatus: async (admin_id: number): Promise<void> => {
    await api.put(`/auth/admins/${admin_id}/toggle-status`);
  },

  deleteAdmin: async (admin_id: number): Promise<void> => {
    await api.delete(`/auth/admins/${admin_id}`);
  },

  changePassword: async (data: { current_password: string; new_password: string }): Promise<void> => {
    await api.put('/auth/change-password', data);
  },

};

export const usersApi = {
  list: async (params?: { skip?: number; limit?: number; status?: string; search?: string }): Promise<Paginated<User>> => {
    const response = await api.get('/users', { params });
    const headerTotal = response.headers['x-total-count'];
    const total = headerTotal !== undefined ? parseInt(headerTotal, 10) : response.data.length;
    return { items: response.data, total: Number.isFinite(total) ? total : response.data.length };
  },

  create: async (data: { first_name: string; last_name: string; email: string; country: string; timezone: string }): Promise<User> => {
    const response = await api.post('/users', data);
    return response.data;
  },

  get: async (id: number): Promise<User> => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  update: async (id: number, data: { first_name?: string; last_name?: string; email?: string; country?: string; timezone?: string; auto_reminder_enabled?: boolean; auto_reminder_days?: number }): Promise<User> => {
    const response = await api.put(`/users/${id}`, data);
    return response.data;
  },

  bulkSetAutoReminder: async (
    data: { user_ids: number[]; enabled: boolean; days?: number },
  ): Promise<{ updated: number; not_found: number[]; reachable: number }> => {
    const response = await api.put('/users/bulk/auto-reminder', data);
    return response.data;
  },

  updateStatus: async (id: number, status: string): Promise<void> => {
    await api.put(`/users/${id}/status`, { status });
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/users/${id}`);
  },

  getDeletionImpact: async (id: number): Promise<UserDeletionImpact> => {
    const response = await api.get(`/users/${id}/deletion-impact`);
    return response.data;
  },

  createRegistrationCode: async (data?: { expires_days?: number }): Promise<RegistrationCode> => {
    const response = await api.post('/users/registration-codes', data || {});
    return response.data;
  },

  listRegistrationCodes: async (params?: { skip?: number; limit?: number; status?: string }): Promise<RegistrationCode[]> => {
    const response = await api.get('/users/registration-codes', { params });
    return response.data;
  },

  revokeRegistrationCode: async (id: number): Promise<void> => {
    await api.put(`/users/registration-codes/${id}/revoke`);
  },

  getAvailableForAssignment: async (params?: { skip?: number; limit?: number }): Promise<User[]> => {
    console.log('DEBUG: Calling getAvailableForAssignment with params:', params);
    console.log('DEBUG: API base URL:', api.defaults.baseURL);
    console.log('DEBUG: Auth token:', localStorage.getItem('accessToken') ? 'Present' : 'Missing');
    
    try {
      const response = await api.get('/users/available-for-assignment', { params });
      console.log('DEBUG: getAvailableForAssignment response:', response.data);
      return response.data;
    } catch (error) {
      console.error('DEBUG: getAvailableForAssignment error:', error);
      console.error('DEBUG: Error response:', error.response?.data);
      console.error('DEBUG: Error status:', error.response?.status);
      console.error('DEBUG: Error config:', error.config);
      throw error;
    }
  },
};

export const surveysApi = {
  list: async (params?: { skip?: number; limit?: number; status?: string }): Promise<Survey[]> => {
    const response = await api.get('/surveys', { params });
    return response.data;
  },

  exportSurveyToExcel: async (surveyId: number): Promise<Blob> => {
    const response = await api.get(`/surveys/${surveyId}/export/excel`, {
      responseType: 'blob',
    });
    return response.data;
  },

  create: async (data: { title: string; description?: string; periodicity_days?: number; portfolio_ref?: string }): Promise<Survey> => {
    const response = await api.post('/surveys', data);
    return response.data;
  },

  get: async (id: number): Promise<Survey> => {
    const response = await api.get(`/surveys/${id}`);
    return response.data;
  },

  update: async (id: number, data: Partial<Survey>): Promise<Survey> => {
    const response = await api.put(`/surveys/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/surveys/${id}`);
  },

  publish: async (id: number): Promise<void> => {
    await api.put(`/surveys/${id}/publish`);
  },

  archive: async (id: number): Promise<void> => {
    await api.put(`/surveys/${id}/archive`);
  },

  getAvailableForAssignment: async (): Promise<Survey[]> => {
    const response = await api.get('/surveys/available-for-assignment');
    return response.data;
  },

  getMySurvey: async (telegram_user_code: string): Promise<Survey | null> => {
    const response = await api.get('/surveys/my-survey', {
      params: { telegram_user_code }
    });
    return response.data;
  },
};

export const questionsApi = {
  list: async (params?: { survey_id?: number; skip?: number; limit?: number }): Promise<Question[]> => {
    const response = await api.get('/questions', { params });
    return response.data;
  },

  create: async (data: {
    survey_id: number;
    type: string;
    text: string;
    answer_criteria?: string;
    instructions?: string;
    order?: number;
    is_required?: boolean;
  }): Promise<Question> => {
    const response = await api.post('/questions', data);
    return response.data;
  },

  get: async (id: number): Promise<Question> => {
    const response = await api.get(`/questions/${id}`);
    return response.data;
  },

  update: async (id: number, data: Partial<Question>): Promise<Question> => {
    const response = await api.put(`/questions/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/questions/${id}`);
  },

  reorder: async (reorderData: { question_id: number; new_order: number }[]): Promise<void> => {
    await api.put('/questions/reorder', reorderData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  },

  test: async (id: number, sample_message: string): Promise<any> => {
    const response = await api.post(`/questions/${id}/test`, { sample_message });
    return response.data;
  },

  getTypeTemplates: async (): Promise<any> => {
    const response = await api.get('/questions/types/criteria-templates');
    return response.data;
  },
};

export const botContextApi = {
  list: async (params?: { survey_id?: number; status?: string; skip?: number; limit?: number }): Promise<ContextVersion[]> => {
    const response = await api.get('/bot-context', { params });
    return response.data;
  },

  create: async (data: {
    survey_id: number;
    context_fields_json?: any;
    context_notes_md?: string;
    attachments?: string[];
  }): Promise<ContextVersion> => {
    const response = await api.post('/bot-context', data);
    return response.data;
  },

  get: async (id: number): Promise<ContextVersion> => {
    const response = await api.get(`/bot-context/${id}`);
    return response.data;
  },

  update: async (id: number, data: Partial<ContextVersion>): Promise<ContextVersion> => {
    const response = await api.put(`/bot-context/${id}`, data);
    return response.data;
  },

  preview: async (id: number): Promise<void> => {
    await api.put(`/bot-context/${id}/preview`);
  },

  activate: async (id: number): Promise<void> => {
    await api.put(`/bot-context/${id}/activate`);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/bot-context/${id}`);
  },

  getFieldsTemplate: async (): Promise<any> => {
    const response = await api.get('/bot-context/templates/fields');
    return response.data;
  },

  testPrompt: async (id: number, user_message: string): Promise<any> => {
    const response = await api.post(`/bot-context/${id}/test-prompt`, { user_message });
    return response.data;
  },

  getHistory: async (id: number): Promise<ContextVersionHistory[]> => {
    const response = await api.get(`/bot-context/${id}/history`);
    return response.data;
  },
};

export const assignmentsApi = {
  list: async (params?: {
    user_id?: number;
    survey_id?: number;
    status?: string;
    skip?: number;
    limit?: number;
  }): Promise<Assignment[]> => {
    const response = await api.get('/assignments', { params });
    return response.data;
  },

  create: async (data: { user_id: number; survey_id: number }): Promise<Assignment> => {
    const response = await api.post('/assignments', data);
    return response.data;
  },

  createBulk: async (data: { user_ids: number[]; survey_id: number }): Promise<BulkAssignmentResponse> => {
    const response = await api.post('/assignments/bulk', data);
    return response.data;
  },

  get: async (id: number): Promise<Assignment> => {
    const response = await api.get(`/assignments/${id}`);
    return response.data;
  },

  update: async (id: number, data: { status?: string }): Promise<Assignment> => {
    const response = await api.put(`/assignments/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/assignments/${id}`);
  },

  reassign: async (id: number, new_survey_id: number): Promise<void> => {
    await api.put(`/assignments/${id}/reassign`, { new_survey_id });
  },

  getProgressSummary: async (survey_id?: number): Promise<any> => {
    const response = await api.get('/assignments/progress/summary', {
      params: survey_id ? { survey_id } : {},
    });
    return response.data;
  },
};

export const analyticsApi = {
  getOverview: async (): Promise<AnalyticsOverview> => {
    const response = await api.get('/analytics/overview');
    return response.data;
  },

  getSurveyAnalytics: async (): Promise<SurveyAnalytics[]> => {
    const response = await api.get('/analytics/surveys');
    return response.data;
  },

  getUserEngagement: async (days: number = 30): Promise<UserEngagement[]> => {
    const response = await api.get('/analytics/engagement', { params: { days } });
    return response.data;
  },

  getResponsesData: async (params?: {
    survey_id?: number;
    user_id?: number;
    start_date?: string;
    end_date?: string;
    skip?: number;
    limit?: number;
  }): Promise<any[]> => {
    const response = await api.get('/analytics/responses', { params });
    return response.data;
  },

  exportResponsesCSV: async (params?: any): Promise<Blob> => {
    const response = await api.get('/analytics/export/responses/csv', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  exportResponsesExcel: async (params?: any): Promise<Blob> => {
    const response = await api.get('/analytics/export/responses/xlsx', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  getUsageStats: async (): Promise<any> => {
    const response = await api.get('/analytics/usage-stats');
    return response.data;
  },
};

export const filesApi = {
  listUploads: async (params?: {
    user_id?: number;
    survey_id?: number;
    skip?: number;
    limit?: number;
  }): Promise<UploadFile[]> => {
    const response = await api.get('/files/uploads', { params });
    return response.data;
  },

  uploadFile: async (file: File, user_id: number, survey_id?: number): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const params = new URLSearchParams({ user_id: user_id.toString() });
    if (survey_id) {
      params.append('survey_id', survey_id.toString());
    }

    const response = await api.post(`/files/upload?${params}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  downloadFile: async (id: number): Promise<Blob> => {
    const response = await api.get(`/files/uploads/${id}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  deleteUpload: async (id: number): Promise<void> => {
    await api.delete(`/files/uploads/${id}`);
  },

  listMessageLogs: async (params?: {
    user_id?: number;
    direction?: string;
    message_type?: string;
    skip?: number;
    limit?: number;
  }): Promise<MessageLog[]> => {
    const response = await api.get('/files/message-logs', { params });
    return response.data;
  },

  purgeData: async (data: {
    user_id?: number;
    survey_id?: number;
    older_than_days?: number;
    file_types?: string[];
  }): Promise<any> => {
    const response = await api.post('/files/purge', data);
    return response.data;
  },

  getStorageStats: async (): Promise<any> => {
    const response = await api.get('/files/storage-stats');
    return response.data;
  },
};


export interface QuestionWithResponse {
  question_id: number;
  question_text: string;
  question_type: string;
  question_order: number;
  is_required: boolean;
  response_value: string | null;
  response_confidence: number | null;
  response_created_at: string | null;
  /** The user was asked but explicitly gave no reflection on this question. */
  declined: boolean;
}

export interface UserSurveyStatus {
  user_id: number;
  user_name: string;
  user_email: string;
  assignment_status: string;
  assigned_at: string;
  completed_at: string | null;
  session_number: number | null;
  total_sessions: number | null;
  /** Project/workstream this reflection is about. */
  subject_label: string | null;
  reflection_status: string | null;
  total_questions: number;
  answered_questions: number;
  declined_questions: number;
  progress_percentage: number;
  questions: QuestionWithResponse[];
}

export interface SurveyStatusResponse {
  survey_id: number;
  survey_title: string;
  survey_description: string | null;
  total_questions: number;
  total_assigned_users: number;
  users: UserSurveyStatus[];
}

export const surveyStatusApi = {
  getSurveyStatus: async (surveyId: number): Promise<SurveyStatusResponse> => {
    const response = await api.get(`/survey-status/${surveyId}`);
    return response.data;
  },
};

export default api;
